import React, { useState } from "react";
import styles from "./Submission.module.scss";
import IconButton from "../../../../components/ButtonIcon/ButtonIcon";
import ems_logo from "../../../../../../images/v2/ems.png";
import nuvei_logo from "../../../../../../images/v2/nuvei.png";
import nab_logo from "../../../../../../images/v2/nab.png";
import priority_logo from "../../../../../../images/v2/priority.png";
import StatusDot from "../../../../components/StatusDot/StatusDot";
import JSONPretty from "react-json-pretty";
import moment from "moment";
import ChevronRight16 from "../../../../assets/svg/icons/common/ChevronRight16";
import CaretDown from "../../../../assets/svg/icons/common/CaretDown";
import FileIcon from "../../../../assets/svg/icons/common/file";
import kurv_logo from "../../../../../../images/v2/kurv-logo.png";
import useUserStore from "../../../../stores/user";
import KebabIcon from "../../../../assets/svg/icons/common/kebab";
import DropDown from "../../../../components/DropDown/DropDown";
import UploadPaylianceDocument from "./UploadPaylianceDocument";

/**
 * `documents`, `data` and `response` are not always JSON text. For an
 * application past the retention window SubmissionController::sendServiceHistory
 * hides all three, so the key is absent rather than empty — and the array form
 * of the same withholding (Powerapp::withholdPii) leaves the string "Redacted"
 * in its place. A row can also simply predate a bank reply and carry null.
 *
 * An unguarded JSON.parse takes the whole tab down on the first of those
 * (JSON.parse(undefined) parses the string "undefined"), so anything unparseable
 * falls back and the audit trail — service, date, statuses — still renders.
 */
const parseServiceHistoryJson = (value, fallback = null) => {
  if (typeof value !== "string" || value === "") return fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

function SubmittedItem({ history, appData }) {
  const [expanded, setExpanded] = useState(false);
  const [expandResponse, setExpandResponse] = useState(false);
  const [uploadDocsOpen, setUploadDocsOpen] = useState(false);
  const isPCAdmin = useUserStore((state) => state.isPCAdmin);

  const isPayliance = history?.service?.toLowerCase() === "payliance";

  const dropdownOptions = [
    ...(isPayliance
      ? [
          {
            text: "Upload Payliance Docs",
            onClick: () => setUploadDocsOpen(true),
          },
        ]
      : []),
  ];

  const getLogo = (processor) => {
    if (processor == "ems") return ems_logo;
    if (processor == "ems direct") return ems_logo;
    if (processor == "nuvei") return nuvei_logo;
    if (processor == "nab") return nab_logo;
    if (processor == "priority") return priority_logo;
    if (processor == "kurv") return kurv_logo;
  };

  const getStatusColor = (status) => {
    if (!status) return "#F4BA3B"; // Default yellow for pending

    const statusLower = status.toLowerCase();

    // Approved statuses - Green
    if (['approved', 'appr rsv', 'underwriting approved-client successfully boarded'].includes(statusLower)) {
      return "#34a853";
    }

    // Declined statuses - Red
    if (['declined', 'rejected/declined'].includes(statusLower)) {
      return "#ea4335";
    }

    // Cancelled - Dark red
    if (['cancelled'].includes(statusLower)) {
      return "#660000";
    }

    // Underwriting/Review statuses - Blue
    if (['underwriting', 'at bank', 'under mngmt review', 'final review', 'udw final review', 'pendingdocuments'].includes(statusLower)) {
      return "#26A1F2";
    }

    // Held status - Orange
    if (['held'].includes(statusLower)) {
      return "#ff6d01";
    }

    // Default pending status - Yellow
    return "#F4BA3B";
  };

  const getApiStatusColor = (status) => {
    switch (status) {
      case 1:
        return "#34a853"; // Sent - Green
      case 2:
        return "#F4BA3B"; // Pending - Yellow
      case 0:
        return "#ea4335"; // Failed - Red
      default:
        return "#F4BA3B"; // Pending - Yellow
    }
  };

  const status = history ? history.status : null;

  const submittedDocuments = parseServiceHistoryJson(history?.documents, []);
  const submittedData = parseServiceHistoryJson(history?.data);
  const submittedResponse = parseServiceHistoryJson(history?.response);
  const application_status = history ? history.application_status : null;

  return (
    <>
      <div className={styles.submission_item}>
        <div className={styles.item_head}>
          <input type="checkbox" />
          <IconButton onClick={() => setExpanded(!expanded)}>
            <ChevronRight16 />
          </IconButton>
        </div>

        <div className={styles.body}>
          <div className={styles.processor}>
            <img src={getLogo(history.service)} />
            <h6>{history.service}</h6>
          </div>

          <div className={styles.action}>
            <p>
              Sent: {moment(history.created_at).format("MMM D, YYYY | H:mm A")}
            </p>

            {dropdownOptions.length > 0 && (
              <DropDown size="small" options={dropdownOptions}>
                <KebabIcon />
              </DropDown>
            )}
          </div>
        </div>
      </div>

      <div
        className={`${styles.submission_data} ${expanded ? styles.expanded : ""}`}
      >
        {expanded && (
          <>
            <div className={styles.statuses}>
              <div className={styles.status_item}>
                <span className={styles.name}>API Status</span>
                <div className={styles.status_color}>
                  <StatusDot color={getApiStatusColor(status)} />
                  <span>
                    {status != null
                      ? status == 1
                        ? "Sent"
                        : status == 2
                          ? "Pending"
                          : "Failed"
                      : "-"}
                  </span>
                </div>
              </div>

              <div className={styles.status_item}>
                <span className={styles.name}>Application Status</span>
                <div className={styles.status_color}>
                  <StatusDot color={getStatusColor(application_status)} />
                  <span>{application_status || "-"}</span>
                </div>
              </div>
            </div>

            {isPayliance && (
              <div className={styles.statuses}>
                <div className={styles.status_item}>
                  <span className={styles.name}>Status Reason</span>
                  <div className={styles.status_color}>
                    <span>{history?.latest_poll_response?.boardingStatusReason || "-"}</span>
                  </div>
                </div>
              </div>
            )}

            <div className={styles.submission_data_list}>
              {Array.isArray(submittedDocuments) &&
                submittedDocuments.map((doc, index) => (
                  <div className={styles.list_item} key={index}>
                    <FileIcon />
                    <span>{doc}</span>
                  </div>
                ))}
            </div>

            <div className={styles.response_container}>
              <div className={styles.header}>
                <IconButton onClick={() => setExpandResponse(!expandResponse)}>
                  <CaretDown />
                </IconButton>
                <span>Response</span>
              </div>

              {expandResponse && (
                <div className={styles.body}>
                  <div className={styles.submitted_response}>
                    <h6>Submitted Data</h6>
                    <div className={styles.data}>
                      {submittedData ? (
                        <JSONPretty
                          id="json-pretty"
                          data={submittedData}
                        ></JSONPretty>
                      ) : (
                        <span>Not available</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.submitted_response}>
                    <h6>Response</h6>
                    <div className={styles.data}>
                      {submittedResponse ? (
                        <JSONPretty
                          id="json-pretty"
                          data={submittedResponse}
                        ></JSONPretty>
                      ) : (
                        <span>Not available</span>
                      )}
                    </div>
                  </div>

                  {history?.latest_poll_response && (
                    <div className={styles.submitted_response}>
                      <h6>Latest Poll Response</h6>
                      <div className={styles.data}>
                        <JSONPretty
                          id="json-pretty"
                          data={history?.latest_poll_response}
                        ></JSONPretty>
                      </div>
                    </div>
                  )}
                  

                </div>
              )}
            </div>
          </>
        )}
      </div>

      {isPayliance && (
        <UploadPaylianceDocument
          isOpen={uploadDocsOpen}
          onClose={() => setUploadDocsOpen(false)}
          historyId={history.id}
        />
      )}
    </>
  );
}

export default SubmittedItem;
